
import {
  BuilderActions,
  BuilderElement,
  BuilderElementControls,
  ElementDependencyAndCondition,
  NestingRuleTypeV,
  RequiredPluginV
} from "@/types/elements";
import Vue from "vue";
import { Component, Prop, Provide, Watch } from "vue-property-decorator";
import defensiveCopy from "@/util/defensive-copy";
import ElementStudioControls from "./ElementStudioControls.vue";
import * as lodash from "lodash";
import { Control, ControlType } from "@/types/controls";
import CodeInput from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import { configStore } from "@/store/store-accessor";
import { objectGetTypedKeys } from "@/util/type-assertions";
import ElementStudioDependencies from "@/components/ElementStudio/ElementStudioDependencies.vue";
import { getElementWithoutExternalValues } from "@/lib/elements/studio/clean-element";
import ElementStudioAction from "@/components/ElementStudio/ElementStudioAction.vue";
import CopyButton from "@/components/Reusable/CopyButton.vue";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import {
  ElementStudioExpectedDataToLoad,
  ElementStudioSaveLocationWithName
} from "@/types/element-studio";
import {
  getNestingRuleTypesThatAreAllowedInside,
  isNestingRuleAllowedInside
} from "@/lib/tree/nesting";
import ElementStudioDefaultElements from "@/components/ElementStudio/ElementStudioDefaultElements.vue";
import ElementStudioAttributes from "@/components/ElementStudio/ElementStudioAttributes.vue";
import ElementStudioAdditionalClasses from "@/components/ElementStudio/ElementStudioAdditionalClasses.vue";
import ElementStudioDefaultProperties from "./ElementStudioDefaultProperties.vue";
import ElementStudioProjectManagement from "@/components/ElementStudio/ElementStudioProjectManagement.vue";
import { unwrapControlValue } from "@/lib/controls/utils";
import { TAGS_AVAILABLE } from "@/types/tags";
import Icon from "@/components/UI/Icon.vue";
import ToggleInput from "@/components/Reusable/CustomInputs/ToggleInput.vue";
import { iconNames } from "@/lib/icons/icons";
import ElementIcon from "@/components/UI/ElementIcon.vue";
import Accordion from "@/components/UI/Accordion.vue";
import ElementStudioPropertyPaths from "@/components/ElementStudio/ElementStudioPropertyPaths.vue";
import ElementStudioManagePropertyPaths from "@/components/ElementStudio/ElementStudioManagePropertyPaths.vue";
import { COLUMNS_SLUG } from "@/lib/elements/constants";
import ElementStudioIconsAutocomplete from "@/components/ElementStudio/ElementStudioIconsAutocomplete.vue";
import { readFromLocalStorage, writeToLocalStorage } from "@/util/localstorage";
import { getBaseClassNameForBuilderElement } from "@/lib/elements/utils";
import { inputTextValidationRules } from "@/util/input-text-validation-rules";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import colors from "@/sass/modules/color.module.scss";
import {
  getPathToAllControls,
  logNotificationOfSameItem
} from "@/lib/elements/studio/misc";
import { isBreakdanceDevelopmentEnvironment } from "@/lib/dev/development-environment";
import {
  getElementSlug,
  getItemNamespaceFromSlug,
  isValidItemName
} from "@/lib/elements/studio/misc";
import ElementStudioSaveNewDialog from "@/components/ElementStudio/ElementStudioSaveNewDialog.vue";
import { getMacroName } from "@/lib/elements/studio/element-to-php";
import slugify from "slugify";
import { getSlugFromNamespaceAndFilename } from "@/lib/elements/studio/presets";
import { pipe } from "fp-ts/function";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import * as t from "io-ts";
import { fold } from "fp-ts/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import clipboardCopy from "clipboard-copy";
import notifications from "@/lib/notifications/notifications";
import { renderTags } from "@/lib/twig/twig";
import { setTwigMacrosInStoreAndRecompile } from "@/lib/elements/studio/macros";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";

const updateStoreActionsThrottled = lodash.debounce(
  breakdanceStoreActions.registerAllStoreActions,
  // This is a not-UI update so we make it slowish
  500
);

@Component({
  components: {
    Dropdown,
    ElementStudioSaveNewDialog,
    TextInput,
    ElementStudioIconsAutocomplete,
    ElementStudioManagePropertyPaths,
    ElementStudioPropertyPaths,
    Accordion,
    ElementIcon,
    ToggleInput,
    Icon,
    ElementStudioAttributes,
    ElementStudioAdditionalClasses,
    ElementStudioDefaultElements,
    CopyButton,
    ElementStudioAction,
    ElementStudioDependencies,
    ElementStudioControls,
    CodeInput,
    ElementStudioDefaultProperties,
    ElementStudioProjectManagement
  }
})
export default class ElementStudioTabsAndPanels extends Vue {
  @Prop()
  element!: BuilderElement;
  @Prop({ default: "primary" })
  type!: "primary" | "secondary";

  @Prop()
  available!: ElementStudioExpectedDataToLoad;

  // Remember the last used tabs
  tab = readFromLocalStorage(`element-studio-tab-${this.type}`) || "content";

  actionsAccordionState = "";
  nameHasChanged = false;
  useLocalIcon =
    !!iconNames.find(iconName => iconName === this.element.uiIcon) ||
    !this.element.uiIcon ||
    false;

  contextMenu: { x: number; y: number } | false = false;
  showNewMacroDialog = false;
  savedCssSelection = "";

  async mounted() {
    document.addEventListener("contextmenu", this.handleRightClickInCodeInput);
  }

  beforeDestroy() {
    document.removeEventListener(
      "contextmenu",
      this.handleRightClickInCodeInput
    );
  }

  @Watch("element.name")
  onElementSlugChange() {
    this.nameHasChanged = true;
  }

  handleRightClickInCodeInput(e: MouseEvent) {
    const selectedText = window.getSelection()?.toString();

    if (!(selectedText && (this.tab === "css" || this.tab === "html"))) {
      return;
    }

    // only prevent it if we're going to show something. Otherwise, default.
    e.preventDefault();

    this.contextMenu = {
      x: e.clientX,
      y: e.clientY
    };
  }

  handleCreateMacro() {
    // When we open the dialog, the window loses focus and can't find the selection anymore
    this.savedCssSelection = window.getSelection()?.toString() || "";
    this.showNewMacroDialog = true;
  }

  async saveNewMacro(newItem: ElementStudioSaveLocationWithName) {
    if (!this.savedCssSelection) return;

    this.contextMenu = false;

    const selectedCss = this.savedCssSelection?.toString();

    const macroName = getMacroName(newItem.namespace, newItem.name);
    // Tags such as %%SELECTOR%% are invalid twig and would error out
    // plus it's nice DX
    const macroText = renderTags(selectedCss, {
      selector: "{{ selector }}",
      id: "{{ id }}",
      postId: "{{ postId }}"
    }).replace("macros.", "_self.");

    const macro = `{% macro ${macroName}() %}\n\n${macroText} \n\n{% endmacro %}`;

    const filename = slugify(newItem.name);
    const newSlug = getSlugFromNamespaceAndFilename(
      newItem.namespace,
      newItem.name
    );

    if (this.available.macros.find(macro => macro.slug === newSlug)) {
      logNotificationOfSameItem(this.available.saveLocations, newItem, "macro");

      return;
    }

    pipe(
      await makeWordPressAjaxRequest({
        action: "breakdance_save_macro",
        dataToPost: {
          directoryPath: newItem.directoryPath,
          filename,
          macro
        },
        decoder: t.unknown
      }),
      fold(
        e => {
          console.error(e);
          NotificationLogger.log.message(
            "Failed to save element, check console"
          );
        },
        () => {
          const macroText = `{{ macros.${macroName}() }}`;
          notifications.log.message(`New macro created: ${macroText}. `);
          clipboardCopy(macroText);

          const newData = defensiveCopy(this.available);
          newData.macros.push({
            slug: newSlug,
            directoryPath: newItem.directoryPath
          });
          this.elementStudioContext.updateElementStudioData(newData);

          const newMacros = defensiveCopy(configStore.twigMacros);
          newMacros.push({ macro, slug: newSlug });
          setTwigMacrosInStoreAndRecompile(newMacros);
        }
      )
    );
  }

  updateSlug() {
    if (
      !window.confirm(
        "Changing the slug of an existing element will make it obsolete in all pages, everywhere. Continue anyway?"
      )
    ) {
      return;
    }

    const newSlug = getElementSlug(
      getItemNamespaceFromSlug(this.element.slug),
      this.element.name
    );

    this.updateElementValue("slug", newSlug);
  }

  updateUiOrder(newOrder: unknown) {
    const safeOrder = Number(newOrder);
    if (Number.isNaN(safeOrder)) {
      this.updateElementValue("order", 0);
    } else {
      this.updateElementValue("order", safeOrder);
    }
  }

  updateUiIcon(newIcon = "SquareIcon") {
    this.updateElementValue("uiIcon", newIcon);
  }

  updateUseLocalIcon(newUseLocalIcon: boolean) {
    this.useLocalIcon = newUseLocalIcon;

    if (!newUseLocalIcon) {
      this.updateUiIcon("");
    }
  }

  updateClassName(newClassName: string | undefined) {
    if (this.htmlClassNameRule(newClassName) !== true) return;

    this.updateElementValue("className", newClassName || false);
  }

  htmlClassNameRule(value: string | undefined) {
    if (!value) return true;

    return inputTextValidationRules.validateHtmlClassName(value);
  }

  isValidItemName(newName: string) {
    return isValidItemName(newName);
  }

  updateElementName(value: string) {
    if (isValidItemName(value) !== true) return;

    const newElement = defensiveCopy(this.element);

    newElement.name = value;

    this.emit(newElement);
  }

  updateElementValue(path: string, value: unknown) {
    const newElement = defensiveCopy(this.element);
    const newValue = unwrapControlValue(value);
    lodash.set(newElement, path, newValue);

    this.emit(newElement);
  }

  emit(newElement: BuilderElement) {
    this.$emit("input", newElement);
  }

  updateTab(newTab: string) {
    // Update the preferences
    writeToLocalStorage(`element-studio-tab-${this.type}`, newTab);
    this.tab = newTab;
  }

  // We only get the internal controls in the prop
  // So we use this to merge them back before emitting
  mergeElementControlsWithExternalControls(
    newElementControls: BuilderElementControls
  ) {
    const elementWithAllControls = defensiveCopy(newElementControls);

    objectGetTypedKeys(this.element.controls).forEach(key => {
      this.element.controls[key].forEach(control => {
        if (control.options.isExternal) {
          elementWithAllControls[key].push(control);
        }
      });
    });

    return elementWithAllControls;
  }

  get showAdvancedFeatures() {
    return (
      isBreakdanceDevelopmentEnvironment() ||
      configStore.isElementStudioPowerUser
    );
  }

  get requiredPluginItems() {
    return RequiredPluginV.types.map(type => type.value);
  }

  get tags() {
    return TAGS_AVAILABLE;
  }

  get pathsToAllControls() {
    return this.elementStudioContext.getPathsToAllControls();
  }

  get nestingRuleTypeIsContainer() {
    return (
      getNestingRuleTypesThatAreAllowedInside(
        this.cleanElement.nestingRule.type
      ).length > 0 ||
      this.cleanElement.nestingRule.type === "container-restricted"
    );
  }

  get cleanElement() {
    return getElementWithoutExternalValues(this.element);
  }

  get elementCategories() {
    return configStore.builderElementCategories.map(category => ({
      text: category.label,
      value: category.slug
    }));
  }

  get badgeColors() {
    return Object.entries(colors).map(([k]) => {
      const correctValue = k
        .replace(/brand_/g, "brand")
        .replace(/_dark|_light|breakdance_/g, "");

      return {
        text: correctValue,
        value: `var(--${correctValue})`
      };
    });
  }

  get elementType() {
    return NestingRuleTypeV.types.map(type => type.value);
  }

  get containerRestrictedElements() {
    return Object.values(configStore.elements)
      .filter(
        element =>
          element?.nestingRule.type === "container-restricted" &&
          element?.slug !== COLUMNS_SLUG
      )
      .map(element => element?.slug);
  }

  get elementsThisElementIsAllowedToBeInsideOf() {
    return Object.values(configStore.elements)
      .filter(
        element =>
          element &&
          isNestingRuleAllowedInside(
            this.element.nestingRule,
            element.nestingRule,
            element.slug
          )
      )
      .map(element => element?.slug);
  }

  get allElementSlugs() {
    return Object.values(configStore.elements).map(element => element?.slug);
  }

  get elementClass() {
    return `.${getBaseClassNameForBuilderElement(this.element)}`;
  }

  parentControls(path: string) {
    // The path could lead to a Control (`${path}.children[${i}]`)
    // Or a Control[] (designSections)
    return defensiveCopy(
      lodash.get(this.element.controls, path) as Control | Control[]
    );
  }

  @Provide("context")
  elementStudioContext = {
    replaceControls: (path: string, newControls: Control[]) => {
      const newElement = defensiveCopy(this.element);
      lodash.set(newElement.controls, path, newControls);
      this.emit(newElement);
    },
    pasteControl: (path: string, controlToPaste: Control) => {
      const newElement = defensiveCopy(this.element);

      const currentControls = lodash.get(newElement.controls, path);
      currentControls.push(controlToPaste);

      lodash.set(newElement.controls, path, currentControls);
      this.emit(newElement);
    },
    addControl: (path: string, controlType: ControlType = "text") => {
      const newElement = defensiveCopy(this.element);
      const newControl: Control = {
        slug: controlType === "section" ? "new_section" : "new_control",
        label: controlType === "section" ? "New Section" : "New Control",
        keywords: [],
        options: {
          type: controlType
        },
        enableMediaQueries: false,
        enableHover: false,
        children: []
      };

      if (
        path.startsWith("contentSections") ||
        controlType === "text" ||
        controlType === "number"
      ) {
        newControl.options.layout = "vertical";
      }

      // Use the clean controls to push stuff of top of the internal and not external ones
      const internalElementControls = this.cleanElement.controls;
      const parentControl = lodash.get(internalElementControls, path) as
        | Control
        | Control[];

      if (Array.isArray(parentControl)) {
        parentControl.push(newControl);
      } else {
        parentControl.children.push(newControl);
      }

      lodash.set(internalElementControls, path, parentControl);

      newElement.controls = this.mergeElementControlsWithExternalControls(
        internalElementControls
      );

      this.emit(newElement);
    },
    addPreset: (path: string) => {
      const newElement = defensiveCopy(this.element);
      const presetSection = defensiveCopy(
        this.elementStudioContext.getPresetSection(
          "EssentialElements\\typography"
        )
      );

      if (!presetSection) return;

      presetSection.section.options.sectionOptions = {
        ...presetSection.section.options.sectionOptions,
        type: "popout"
      };

      // Use the clean controls to push stuff of top of the internal and not external ones
      const internalElementControls = this.cleanElement.controls;
      const parentControl = lodash.get(internalElementControls, path) as
        | Control
        | Control[];

      if (Array.isArray(parentControl)) {
        parentControl.push(presetSection.section);
      } else {
        parentControl.children.push(presetSection.section);
      }

      lodash.set(internalElementControls, path, parentControl);

      newElement.controls = this.mergeElementControlsWithExternalControls(
        internalElementControls
      );

      this.emit(newElement);
    },
    deleteControl: (parentPath: string, index: number) => {
      const newElement = defensiveCopy(this.element);
      const parentControl = this.parentControls(parentPath);

      if (Array.isArray(parentControl)) {
        parentControl.splice(index, 1);
      } else {
        parentControl.children.splice(index, 1);
      }

      lodash.set(newElement.controls, parentPath, parentControl);

      this.emit(newElement);
    },
    updateControl: (parentPath: string, index: number, newControl: Control) => {
      const newElement = defensiveCopy(this.element);
      const parentControl = this.parentControls(parentPath);

      if (Array.isArray(parentControl)) {
        parentControl.splice(index, 1, newControl);
      } else {
        parentControl.children.splice(index, 1, newControl);
      }

      lodash.set(newElement.controls, parentPath, parentControl);

      this.emit(newElement);
    },
    updateDependency: (newDependencies: ElementDependencyAndCondition[]) => {
      this.updateElementValue("dependencies", newDependencies);
    },
    updateActions: (newActions: BuilderActions) => {
      this.updateElementValue("actions", newActions);
      updateStoreActionsThrottled();
    },
    getPresetSection: (slug: string) => {
      return this.available.presetSections.find(preset => preset.slug === slug);
    },
    getPresetSections: () => {
      return this.available.presetSections;
    },
    getPathsToAllControls: (
      controlTypes: ControlType[] = [],
      controlTypesToIgnore: ControlType[] = ["section"]
    ) => {
      const contentPaths = getPathToAllControls(
        this.element.controls.contentSections,
        controlTypes,
        controlTypesToIgnore,
        "content"
      );

      const designPaths = getPathToAllControls(
        this.element.controls.designSections,
        controlTypes,
        controlTypesToIgnore,
        "design"
      );

      return [...contentPaths, ...designPaths];
    },
    element: () => {
      return this.element;
    },
    updateElementValue: this.updateElementValue,
    getElementStudioData: () => this.available,
    updateElementStudioData: (newData: ElementStudioExpectedDataToLoad) => {
      this.update(newData);
    }
  };

  update(newData: ElementStudioExpectedDataToLoad) {
    this.$emit("update", newData);
  }

  toggleActionAccordion(state: string) {
    this.actionsAccordionState = state;
  }

  get htmlTags() {
    return (
      [
        "section",
        "div",
        "span",
        "p",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "footer",
        "header",
        "nav",
        "aside",
        "figure",
        "figcaption",
        "ul",
        "ol",
        "li",
        "a",
        "article",
        "main",
        "details",
        "summary",
        "img"
      ]
        // No point in selecting the default
        .filter(tag => tag !== this.cleanElement.htmlTag.default)
    );
  }

  get defaultTag() {
    // Add the default in case it's not in htmlTags. e.g "section" for Sections
    return [...this.htmlTags, this.cleanElement.htmlTag.default];
  }
}

export type ElementStudioContext =
  typeof ElementStudioTabsAndPanels.prototype.elementStudioContext;
